import React, { useEffect, useState, useRef, useCallback } from "react";
import Swal from "sweetalert2";
import { Check, X, ChevronDown } from "lucide-react";
import { customPackageApi, Permission } from "../../repositories/customPackage";
import InviteCustomSubscriptionModal from "../../components/modal/InviteCustomSubscriptionModal";
import { useCustomPackage } from "../../hooks/useCustomPackage";
import type { CustomPackage } from "../../store/slices/customPackage";
import PageHeader from "../../components/ui/PageHeader";
import { HOME_CRUMB } from "../../components/ui/Breadcrumbs";
import Button from "../../components/ui/Button";
import DataTable, { DataTableColumn } from "../../components/ui/DataTable";
import ToggleSwitch from "../../components/ui/ToggleSwitch";
import { badgeBaseClass, badgeTones } from "../../components/ui/StatusBadge";
import { SkeletonText } from "../../components/ui/skeleton";
import {
  formCheckboxClass,
  formInputClass,
  formSectionClass,
  formSectionHeadingClass,
} from "../../components/ui/formStyles";
import { cn } from "../../DashboardPage/lib/utils";

interface Plan {
  id: string;
  name: string;
  price: number;
  interval?: string;
}

const cardClass = formSectionClass;
const cardHeadingClass = `mb-4 ${formSectionHeadingClass}`;
const inputClass = formInputClass;
const checkboxClass = formCheckboxClass;

/**
 * A yes/no capability in the packages table. Three adjacent access columns read
 * as a capability matrix, so they scan faster as marks than as three repeated
 * words — but each still carries its text for screen readers. "Not configured"
 * (an older package with no entry for the permission) stays a dash so it can't
 * be mistaken for an explicit "No".
 */
const AccessCell: React.FC<{ value: string }> = ({ value }) => {
  if (value === "Yes") {
    return (
      <span title="Yes">
        <Check size={16} className="inline-block text-success-600" aria-hidden="true" />
        <span className="sr-only">Yes</span>
      </span>
    );
  }
  if (value === "No") {
    return (
      <span title="No">
        <X size={16} className="inline-block text-neutral-400" aria-hidden="true" />
        <span className="sr-only">No</span>
      </span>
    );
  }
  return (
    <span className="text-neutral-300" title="Not configured">
      —<span className="sr-only">Not configured</span>
    </span>
  );
};

/**
 * A numeric limit. Kept as text rather than a mark: unlike the access columns
 * these carry a real value, and "Unlimited" is a meaningful state that no icon
 * would convey.
 */
const LimitCell: React.FC<{ value: string }> = ({ value }) => {
  if (value === "-" || !value) {
    return (
      <span className="text-neutral-300" title="Not configured">
        —<span className="sr-only">Not configured</span>
      </span>
    );
  }
  if (value === "Unlimited") {
    return <span className="text-sm text-neutral-600">Unlimited</span>;
  }
  return <span className="tabular-nums">{value}</span>;
};

const CustomSubscription = () => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingPermissions, setIsLoadingPermissions] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState<CustomPackage | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const initialEditValues = {
    planName: "",
    packageId: "",
    aimsAccess: false,
    apiAccess: false,
    emailAccess: false,
    pitchLimit: "",
    pitchUnlimited: false,
    rosterLimit: "",
    rosterUnlimited: false,
  };
  const [editValues, setEditValues] = useState(initialEditValues);
  // Snapshot of editValues at the moment the modal opened, used to detect
  // whether the user actually changed anything before allowing a save.
  const originalEditValues = useRef<typeof initialEditValues | null>(null);

  // Custom packages — single source of truth in Redux, shared with the invite modal
  const {
    packages: customPackages,
    loading: isLoadingPackages,
    fetchCustomPackages,
  } = useCustomPackage();

  // Permission values state
  const initialPermissionValues = {
    aimsAccess: false,
    apiAccess: false,
    emailAccess: false,
    pitchLimit: "",
    pitchUnlimited: false,
    rosterLimit: "",
    rosterUnlimited: false,
  };
  const [permissionValues, setPermissionValues] = useState(initialPermissionValues);

  // Inline validation: which limit inputs failed (drives a red border instead
  // of a popup). Keyed by field name; true = invalid.
  const [createErrors, setCreateErrors] = useState({ pitchLimit: false, rosterLimit: false });
  const [editErrors, setEditErrors] = useState({ pitchLimit: false, rosterLimit: false });

  // Fetch Stripe products. Extracted so it can be re-run after creating a
  // package (the backend only returns products not yet configured, so the
  // dropdown must refresh once a product is consumed).
  const fetchStripeProducts = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await customPackageApi.getAllStripeProducts();

      // Handle API response structure: { success: true, data: [...] }
      let products = [];
      if (response.data?.success && response.data?.data) {
        products = response.data.data;
      } else if (Array.isArray(response.data)) {
        products = response.data;
      } else {
        products = [];
      }


      // Extract id, name, price, and interval from products
      const extractedPlans: Plan[] = products.map((product: any) => ({
        id: product.id,
        name: product.name,
        price: product.prices?.[0]?.unit_amount || 0,
        interval: product.prices?.[0]?.recurring?.interval || null,
      }));

      setPlans(extractedPlans);
    } catch (error: any) {
      console.error("Failed to fetch Stripe products:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch Stripe products on component mount
  useEffect(() => {
    fetchStripeProducts();
  }, [fetchStripeProducts]);

  // Fetch permissions on component mount
  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        setIsLoadingPermissions(true);
        const response = await customPackageApi.getAllPermissions();


        // Extract permissions from response
        let permissionsData = [];
        if (response.data?.success && response.data?.data) {
          permissionsData = response.data.data;
        } else if (Array.isArray(response.data)) {
          permissionsData = response.data;
        } else {
          permissionsData = [];
        }

        setPermissions(permissionsData);
      } catch (error: any) {
        console.error("Failed to fetch permissions:", error);
      } finally {
        setIsLoadingPermissions(false);
      }
    };

    fetchPermissions();
  }, []);

  // Fetch custom packages on component mount (via Redux)
  useEffect(() => {
    fetchCustomPackages();
  }, [fetchCustomPackages]);

  const handlePlanSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const planId = e.target.value;
    if (!planId) {
      setSelectedPlan(null);
      return;
    }

    const plan = plans.find((p) => p.id === planId);
    if (plan) {
      setSelectedPlan(plan);
      console.log("Selected plan:", { id: plan.id, name: plan.name });
    }
  };

  // Get display name for permission
  const getPermissionDisplayName = (permissionName: string): string => {
    const nameMap: { [key: string]: string } = {
      "aims.access": "AIMS Access",
      "headless.access": "API Access",
      "pitches.add": "Pitch Limit",
      "roaster.add": "Roster Limit",
      "email.access": "Email Access",
    };
    return nameMap[permissionName] || permissionName;
  };

  // Get permission key from name
  const getPermissionKey = (permissionName: string): string | null => {
    const keyMap: { [key: string]: string } = {
      "aims.access": "aimsAccess",
      "headless.access": "apiAccess",
      "pitches.add": "pitchLimit",
      "roaster.add": "rosterLimit",
      "email.access": "emailAccess",
    };
    return keyMap[permissionName] || null;
  };

  // Handle toggle change
  const handleToggleChange = (key: string, value: boolean) => {
    setPermissionValues((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // Handle input change
  const handleInputChange = (key: string, value: string) => {
    setPermissionValues((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // Handle submit
  const handleSubmit = async () => {
    // Validate that a plan is selected
    if (!selectedPlan) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Please select a plan first",
      });
      return;
    }

    // When "Unlimited" is unchecked, the limit input must be a valid integer.
    // Flag invalid fields with a red border instead of a popup.
    const pitchInvalid =
      !permissionValues.pitchUnlimited && !Number.isInteger(parseInt(permissionValues.pitchLimit));
    const rosterInvalid =
      !permissionValues.rosterUnlimited && !Number.isInteger(parseInt(permissionValues.rosterLimit));
    if (pitchInvalid || rosterInvalid) {
      setCreateErrors({ pitchLimit: pitchInvalid, rosterLimit: rosterInvalid });
      return;
    }
    setCreateErrors({ pitchLimit: false, rosterLimit: false });

    try {
      setIsSubmitting(true);

      // Build permissions array with limits
      const permissionsPayload = permissions.map((permission) => {
        let limit: number | null = null;

        if (permission.name === "aims.access") {
          // For AIMS Access: null means true (enabled)
          limit = permissionValues.aimsAccess ? null : 0;
        } else if (permission.name === "headless.access") {
          // For API Access: null means true (enabled)
          limit = permissionValues.apiAccess ? null : 0;
        } else if (permission.name === "email.access") {
          // For Email Access: null means true (enabled)
          limit = permissionValues.emailAccess ? null : 0;
        } else if (permission.name === "pitches.add") {
          // For Pitch Limit: -1 if unlimited checkbox is checked, otherwise parse the value
          if (permissionValues.pitchUnlimited) {
            limit = -1;
          } else {
            const parsed = parseInt(permissionValues.pitchLimit);
            limit = isNaN(parsed) || permissionValues.pitchLimit === "" ? -1 : parsed;
          }
        } else if (permission.name === "roaster.add") {
          // For Roster Limit: -1 if unlimited checkbox is checked, otherwise parse the value
          if (permissionValues.rosterUnlimited) {
            limit = -1;
          } else {
            const parsed = parseInt(permissionValues.rosterLimit);
            limit = isNaN(parsed) || permissionValues.rosterLimit === "" ? -1 : parsed;
          }
        }

        return {
          permission_id: permission.id,
          limit: limit,
          permission_name: permission.name,
        };
      });

      const payload = {
        packageId: selectedPlan.id,
        planName: selectedPlan.name,
        packageInterval: selectedPlan.interval || null,
        packageAmount: selectedPlan.price / 100,
        permissions: permissionsPayload,
      };


      await customPackageApi.addPackageLimits(payload);

      // Refresh custom packages list (Redux) so the table and invite modal stay in sync
      fetchCustomPackages();

      // Refresh the Stripe products dropdown — the just-configured product is no
      // longer returned by the backend — and reset the create form for the next one.
      await fetchStripeProducts();
      setSelectedPlan(null);
      setPermissionValues(initialPermissionValues);
      setCreateErrors({ pitchLimit: false, rosterLimit: false });

      Swal.fire({
        icon: "success",
        title: "Success!",
        text: "Package limits added successfully",
      });
    } catch (error: any) {
      console.error("Failed to add package limits:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error?.response?.data?.message || "Failed to add package limits",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (pkg: CustomPackage) => {
    // Look up the package's stored entry for a permission. Returns `undefined`
    // when the package has no entry — e.g. a package created before a permission
    // (like email.access) existed and was never configured for it. We must
    // distinguish "no entry" (never granted → off) from "entry with limit null"
    // (granted → on); collapsing both to null wrongly defaults old packages to on.
    const getPkgPerm = (permName: string) => {
      const perm = permissions.find((p) => p.name === permName);
      if (!perm) return undefined;
      return pkg.permissions?.find((p) => p.permission_id === perm.id);
    };

    // Access toggle is enabled only when an entry exists AND its limit is null.
    // A missing entry → off (handles older packages without the permission).
    const isAccessEnabled = (permName: string): boolean => {
      const pkgPerm = getPkgPerm(permName);
      return pkgPerm ? pkgPerm.limit === null : false;
    };

    // For a numeric-limit permission, resolve the form fields. A missing entry
    // (older package never configured for it) defaults to NOT unlimited with an
    // empty input — matching the create form — instead of being treated as
    // unlimited. "Unlimited" is checked only when an entry exists with limit -1.
    const resolveLimitFields = (permName: string) => {
      const pkgPerm = getPkgPerm(permName);
      if (!pkgPerm) return { value: "", unlimited: false };
      const limit = pkgPerm.limit;
      if (limit === -1 || limit === null) return { value: "", unlimited: true };
      return { value: String(limit), unlimited: false };
    };

    const pitch = resolveLimitFields("pitches.add");
    const roster = resolveLimitFields("roaster.add");

    const nextValues = {
      planName: pkg.plan_name,
      packageId: pkg.package_id,
      aimsAccess: isAccessEnabled("aims.access"),
      apiAccess: isAccessEnabled("headless.access"),
      emailAccess: isAccessEnabled("email.access"),
      pitchLimit: pitch.value,
      pitchUnlimited: pitch.unlimited,
      rosterLimit: roster.value,
      rosterUnlimited: roster.unlimited,
    };

    setEditValues(nextValues);
    setEditErrors({ pitchLimit: false, rosterLimit: false });
    // Remember the values the modal opened with so handleUpdate can skip the
    // API call when nothing was actually changed.
    originalEditValues.current = nextValues;
    setEditingPackage(pkg);
  };

  const handleUpdate = async () => {
    if (!editingPackage) return;

    // Don't hit the API when the user opened the modal and saved without
    // changing anything. Compare against the snapshot taken in handleEdit.
    if (
      originalEditValues.current &&
      JSON.stringify(editValues) === JSON.stringify(originalEditValues.current)
    ) {
      setEditingPackage(null);
      return;
    }

    // When "Unlimited" is unchecked, the limit input must be a valid integer.
    // Flag invalid fields with a red border instead of a popup.
    const pitchInvalid =
      !editValues.pitchUnlimited && !Number.isInteger(parseInt(editValues.pitchLimit));
    const rosterInvalid =
      !editValues.rosterUnlimited && !Number.isInteger(parseInt(editValues.rosterLimit));
    if (pitchInvalid || rosterInvalid) {
      setEditErrors({ pitchLimit: pitchInvalid, rosterLimit: rosterInvalid });
      return;
    }
    setEditErrors({ pitchLimit: false, rosterLimit: false });

    try {
      setIsUpdating(true);

      const permissionsPayload = permissions.map((permission) => {
        let limit: number | null = null;
        if (permission.name === "aims.access") {
          limit = editValues.aimsAccess ? null : 0;
        } else if (permission.name === "headless.access") {
          limit = editValues.apiAccess ? null : 0;
        } else if (permission.name === "email.access") {
          limit = editValues.emailAccess ? null : 0;
        } else if (permission.name === "pitches.add") {
          // For Pitch Limit: -1 (unlimited) when the checkbox is checked OR the
          // input is left empty / invalid; otherwise the parsed value. Matches
          // the create flow's behavior.
          if (editValues.pitchUnlimited) {
            limit = -1;
          } else {
            const parsed = parseInt(editValues.pitchLimit);
            limit = isNaN(parsed) || editValues.pitchLimit === "" ? -1 : parsed;
          }
        } else if (permission.name === "roaster.add") {
          // For Roster Limit: -1 (unlimited) when the checkbox is checked OR the
          // input is left empty / invalid; otherwise the parsed value.
          if (editValues.rosterUnlimited) {
            limit = -1;
          } else {
            const parsed = parseInt(editValues.rosterLimit);
            limit = isNaN(parsed) || editValues.rosterLimit === "" ? -1 : parsed;
          }
        }
        return { permission_id: permission.id, limit, permission_name: permission.name };
      });

      await customPackageApi.updatePackageLimits(editingPackage.id, {
        packageId: editValues.packageId,
        planName: editValues.planName,
        permissions: permissionsPayload,
      });

      // Refresh via Redux so the table AND the invite modal stay in sync
      fetchCustomPackages();
      setEditingPackage(null);

      Swal.fire({
        icon: "success",
        title: "Updated!",
        text: "Package updated successfully",
      });
    } catch (error: any) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error?.response?.data?.message || "Failed to update package",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  // True when the edit form differs from the values the modal opened with.
  // Drives the Save button's enabled state so an unchanged package can't be saved.
  const isEditDirty =
    !!originalEditValues.current &&
    JSON.stringify(editValues) !== JSON.stringify(originalEditValues.current);

  // Helper function to get permission name
  const getPermissionNameById = (permissionId: number): string => {
    const permission = permissions.find((p) => p.id === permissionId);
    if (!permission) return "Unknown";
    return getPermissionDisplayName(permission.name);
  };

  // Helper function to format limit value
  const formatLimitValue = (permissionId: number, limit: number | null): string => {
    const permission = permissions.find((p) => p.id === permissionId);
    if (!permission) return "-";

    if (permission.name === "aims.access" || permission.name === "headless.access" || permission.name === "email.access") {
      return limit === null ? "Yes" : "No";
    }

    if (limit === null) return "Yes";
    if (limit === -1) return "Unlimited";
    return limit.toString();
  };

  // Resolve one permission's stored value for a package. "-" means the package
  // has no entry at all — an older package created before the permission existed.
  const getPermissionValue = (pkg: CustomPackage, permName: string): string => {
    const permission = permissions.find((p) => p.name === permName);
    if (!permission) return "-";
    const pkgPerm = pkg.permissions?.find((p) => p.permission_id === permission.id);
    if (!pkgPerm) return "-";
    return formatLimitValue(permission.id, pkgPerm.limit);
  };

  const packageColumns: DataTableColumn<CustomPackage>[] = [
    {
      key: "id",
      header: "ID",
      cell: (_pkg, index) => <span className="tabular-nums text-neutral-600">{index + 1}</span>,
    },
    {
      key: "planName",
      header: "Plan Name",
      cell: (pkg) => <span className="font-medium whitespace-nowrap">{pkg.plan_name}</span>,
    },
    {
      key: "createdAt",
      header: "Created Date",
      cell: (pkg) => (
        <span className="text-neutral-600">
          {pkg.createdAt ? new Date(pkg.createdAt).toLocaleDateString() : "—"}
        </span>
      ),
    },
    {
      key: "rosterLimit",
      header: "Roster Limit",
      align: "center",
      cell: (pkg) => <LimitCell value={getPermissionValue(pkg, "roaster.add")} />,
    },
    {
      key: "pitchLimit",
      header: "Pitch Limit",
      align: "center",
      cell: (pkg) => <LimitCell value={getPermissionValue(pkg, "pitches.add")} />,
    },
    {
      key: "apiAccess",
      header: "API Access",
      align: "center",
      cell: (pkg) => <AccessCell value={getPermissionValue(pkg, "headless.access")} />,
    },
    {
      key: "aimsAccess",
      header: "AIMS Access",
      align: "center",
      cell: (pkg) => <AccessCell value={getPermissionValue(pkg, "aims.access")} />,
    },
    {
      key: "emailAccess",
      header: "Email Access",
      align: "center",
      cell: (pkg) => <AccessCell value={getPermissionValue(pkg, "email.access")} />,
    },
    {
      key: "status",
      header: "Status",
      cell: (pkg) => (
        <span
          className={cn(badgeBaseClass, pkg.status === 1 ? badgeTones.success : badgeTones.neutral)}
        >
          {pkg.status === 1 ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      cell: (pkg) => (
        <Button variant="secondary" size="sm" onClick={() => handleEdit(pkg)}>
          Edit
        </Button>
      ),
    },
  ];

  return (
    <div className="py-2 sm:p-6">
      <PageHeader
        breadcrumbs={[HOME_CRUMB, { label: "Custom Subscription" }]}
        className="mb-6"
        title="Package Management"
        description="Configure subscription packages with custom permissions and limits."
        actions={
          <Button onClick={() => setIsInviteModalOpen(true)} className="w-full sm:w-auto">
            Invite User
          </Button>
        }
      />

      <div className="space-y-6">
        {/* Plan Selection Card */}
        <div className={cardClass}>
          <h2 className={cardHeadingClass}>Select Subscription Plan</h2>

          {isLoading ? (
            <SkeletonText lines={1} className="h-10" />
          ) : (
            <div className="relative">
              <select
                id="plan-select"
                value={selectedPlan?.id || ""}
                onChange={handlePlanSelect}
                className={cn(inputClass, "w-full pr-10 appearance-none cursor-pointer border-neutral-300")}
              >
                <option value="">Select a plan</option>
                {plans
                  .filter((plan) => plan.interval)
                  .map((plan) => (
                    <option key={plan.id} value={plan.id}>
                      {plan.name} - ${(plan.price / 100).toFixed(2)} / {plan.interval}
                    </option>
                  ))}
              </select>
              <ChevronDown className="absolute w-4 h-4 -translate-y-1/2 pointer-events-none right-3 top-1/2 text-neutral-400" />
            </div>
          )}
        </div>

        {/* Permissions Section */}
        <div className={cardClass}>
          <h2 className={cardHeadingClass}>Configure Permissions</h2>

          {isLoadingPermissions ? (
            <div className="space-y-3">
              <SkeletonText lines={1} className="h-16" />
              <SkeletonText lines={1} className="h-16" />
              <SkeletonText lines={1} className="h-16" />
            </div>
          ) : permissions.length === 0 ? (
            <p className="py-8 text-sm text-center text-neutral-500">No permissions found</p>
          ) : (
            <div className="space-y-3">
              {permissions.map((permission) => {
                const displayName = getPermissionDisplayName(permission.name);
                const key = getPermissionKey(permission.name);

                return (
                  <div
                    key={permission.id}
                    className="flex flex-col gap-3 p-4 border rounded-lg sm:flex-row sm:items-center sm:justify-between border-neutral-200 bg-neutral-50"
                  >
                    <span className="text-sm font-medium text-black">{displayName}</span>

                    {/* Access toggles */}
                    {(permission.name === "aims.access" ||
                      permission.name === "headless.access" ||
                      permission.name === "email.access") && key && (
                      <div className="flex items-center gap-3">
                        <span className="text-sm w-8 text-neutral-600">
                          {permissionValues[key as keyof typeof permissionValues] ? "Yes" : "No"}
                        </span>
                        <ToggleSwitch
                          checked={permissionValues[key as keyof typeof permissionValues] as boolean}
                          onChange={(next) => handleToggleChange(key, next)}
                          label={displayName}
                        />
                      </div>
                    )}

                    {/* Input for Pitch Limit */}
                    {permission.name === "pitches.add" && key && (
                      <div className="flex items-center gap-3">
                        <input
                          type="number"
                          value={permissionValues.pitchLimit}
                          onChange={(e) => {
                            handleInputChange("pitchLimit", e.target.value);
                            if (createErrors.pitchLimit) setCreateErrors((p) => ({ ...p, pitchLimit: false }));
                          }}
                          disabled={permissionValues.pitchUnlimited}
                          required={!permissionValues.pitchUnlimited}
                          aria-label="Pitch limit"
                          aria-invalid={createErrors.pitchLimit}
                          className={cn(
                            inputClass,
                            "w-32",
                            createErrors.pitchLimit ? "border-danger-500" : "border-neutral-300"
                          )}
                          placeholder="Enter limit"
                          min="1"
                        />
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={permissionValues.pitchUnlimited}
                            onChange={(e) => {
                              handleToggleChange("pitchUnlimited", e.target.checked);
                              if (e.target.checked) {
                                handleInputChange("pitchLimit", "");
                                setCreateErrors((p) => ({ ...p, pitchLimit: false }));
                              }
                            }}
                            className={checkboxClass}
                          />
                          <span className="text-sm text-neutral-700">Unlimited</span>
                        </label>
                      </div>
                    )}

                    {/* Input for Roster Limit */}
                    {permission.name === "roaster.add" && key && (
                      <div className="flex items-center gap-3">
                        <input
                          type="number"
                          value={permissionValues.rosterLimit}
                          onChange={(e) => {
                            handleInputChange("rosterLimit", e.target.value);
                            if (createErrors.rosterLimit) setCreateErrors((p) => ({ ...p, rosterLimit: false }));
                          }}
                          disabled={permissionValues.rosterUnlimited}
                          required={!permissionValues.rosterUnlimited}
                          aria-label="Roster limit"
                          aria-invalid={createErrors.rosterLimit}
                          className={cn(
                            inputClass,
                            "w-32",
                            createErrors.rosterLimit ? "border-danger-500" : "border-neutral-300"
                          )}
                          placeholder="Enter limit"
                          min="1"
                        />
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={permissionValues.rosterUnlimited}
                            onChange={(e) => {
                              handleToggleChange("rosterUnlimited", e.target.checked);
                              if (e.target.checked) {
                                handleInputChange("rosterLimit", "");
                                setCreateErrors((p) => ({ ...p, rosterLimit: false }));
                              }
                            }}
                            className={checkboxClass}
                          />
                          <span className="text-sm text-neutral-700">Unlimited</span>
                        </label>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Submit Button */}
        <div className="flex justify-end">
          <Button
            onClick={handleSubmit}
            loading={isSubmitting}
            disabled={!selectedPlan || isLoadingPermissions}
          >
            {isSubmitting ? "Submitting..." : "Submit Package Limits"}
          </Button>
        </div>

        {/* Custom Packages List Section */}
        <div>
          <h2 className="mb-4 text-lg font-semibold text-black">Custom Packages</h2>
          <DataTable
            columns={packageColumns}
            rows={customPackages}
            getRowKey={(pkg) => pkg.id}
            loading={isLoadingPackages}
            emptyMessage={
              <span className="block">
                <span className="block text-sm font-medium text-black">No custom packages yet</span>
                <span className="block mt-1 text-sm text-neutral-500">
                  Create your first package to get started.
                </span>
              </span>
            }
          />
        </div>
      </div>

      {/* Invite Custom Subscription Modal */}
      <InviteCustomSubscriptionModal
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
      />

      {/* Edit Package Modal */}
      {editingPackage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="w-full max-w-lg bg-white rounded-lg shadow-lg max-h-[90vh] flex flex-col">
            <h2 className="px-6 pt-6 pb-4 text-lg font-semibold text-black border-b border-neutral-200">
              Edit Package
            </h2>

            <div className="flex-1 px-6 py-4 space-y-4 overflow-y-auto scrollbar-thin">
              <div>
                <label className="block mb-2 text-sm font-medium text-neutral-700">Plan Name</label>
                <input
                  type="text"
                  value={editValues.planName}
                  onChange={(e) => setEditValues((prev) => ({ ...prev, planName: e.target.value }))}
                  className={cn(inputClass, "w-full border-neutral-300")}
                />
              </div>

              {permissions.map((permission) => {
                if (
                  permission.name === "aims.access" ||
                  permission.name === "headless.access" ||
                  permission.name === "email.access"
                ) {
                  const key =
                    permission.name === "aims.access"
                      ? "aimsAccess"
                      : permission.name === "headless.access"
                      ? "apiAccess"
                      : "emailAccess";
                  const displayName = getPermissionDisplayName(permission.name);
                  return (
                    <div
                      key={permission.id}
                      className="flex items-center justify-between p-3 border rounded-lg border-neutral-200 bg-neutral-50"
                    >
                      <span className="text-sm font-medium text-black">{displayName}</span>
                      <div className="flex items-center gap-3">
                        <span className="w-8 text-sm text-neutral-600">
                          {editValues[key as keyof typeof editValues] ? "Yes" : "No"}
                        </span>
                        <ToggleSwitch
                          checked={editValues[key as keyof typeof editValues] as boolean}
                          onChange={(next) => setEditValues((prev) => ({ ...prev, [key]: next }))}
                          label={displayName}
                        />
                      </div>
                    </div>
                  );
                }
                if (permission.name === "pitches.add") {
                  return (
                    <div
                      key={permission.id}
                      className="flex items-center justify-between p-3 border rounded-lg border-neutral-200 bg-neutral-50"
                    >
                      <span className="text-sm font-medium text-black">Pitch Limit</span>
                      <div className="flex items-center gap-3">
                        <input
                          type="number"
                          value={editValues.pitchLimit}
                          onChange={(e) => {
                            setEditValues((prev) => ({ ...prev, pitchLimit: e.target.value }));
                            if (editErrors.pitchLimit) setEditErrors((p) => ({ ...p, pitchLimit: false }));
                          }}
                          disabled={editValues.pitchUnlimited}
                          required={!editValues.pitchUnlimited}
                          aria-label="Pitch limit"
                          aria-invalid={editErrors.pitchLimit}
                          className={cn(
                            inputClass,
                            "w-28",
                            editErrors.pitchLimit ? "border-danger-500" : "border-neutral-300"
                          )}
                          placeholder="Enter limit"
                          min="1"
                        />
                        <label className="flex items-center gap-2 text-sm cursor-pointer text-neutral-700">
                          <input
                            type="checkbox"
                            checked={editValues.pitchUnlimited}
                            onChange={(e) => {
                              setEditValues((prev) => ({ ...prev, pitchUnlimited: e.target.checked, pitchLimit: e.target.checked ? "" : prev.pitchLimit }));
                              if (e.target.checked) setEditErrors((p) => ({ ...p, pitchLimit: false }));
                            }}
                            className={checkboxClass}
                          />
                          Unlimited
                        </label>
                      </div>
                    </div>
                  );
                }
                if (permission.name === "roaster.add") {
                  return (
                    <div
                      key={permission.id}
                      className="flex items-center justify-between p-3 border rounded-lg border-neutral-200 bg-neutral-50"
                    >
                      <span className="text-sm font-medium text-black">Roster Limit</span>
                      <div className="flex items-center gap-3">
                        <input
                          type="number"
                          value={editValues.rosterLimit}
                          onChange={(e) => {
                            setEditValues((prev) => ({ ...prev, rosterLimit: e.target.value }));
                            if (editErrors.rosterLimit) setEditErrors((p) => ({ ...p, rosterLimit: false }));
                          }}
                          disabled={editValues.rosterUnlimited}
                          required={!editValues.rosterUnlimited}
                          aria-label="Roster limit"
                          aria-invalid={editErrors.rosterLimit}
                          className={cn(
                            inputClass,
                            "w-28",
                            editErrors.rosterLimit ? "border-danger-500" : "border-neutral-300"
                          )}
                          placeholder="Enter limit"
                          min="1"
                        />
                        <label className="flex items-center gap-2 text-sm cursor-pointer text-neutral-700">
                          <input
                            type="checkbox"
                            checked={editValues.rosterUnlimited}
                            onChange={(e) => {
                              setEditValues((prev) => ({ ...prev, rosterUnlimited: e.target.checked, rosterLimit: e.target.checked ? "" : prev.rosterLimit }));
                              if (e.target.checked) setEditErrors((p) => ({ ...p, rosterLimit: false }));
                            }}
                            className={checkboxClass}
                          />
                          Unlimited
                        </label>
                      </div>
                    </div>
                  );
                }
                return null;
              })}
            </div>

            <div className="flex justify-end gap-3 px-6 py-4 border-t rounded-b-lg border-neutral-200 bg-neutral-50">
              <Button variant="neutral" onClick={() => setEditingPackage(null)}>
                Cancel
              </Button>
              <Button onClick={handleUpdate} loading={isUpdating} disabled={!isEditDirty}>
                {isUpdating ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomSubscription;
